import React, { useMemo, useCallback } from 'react';
import AntLocalization from '../../utils/AntLocalization';

function formatHour(h) {
    var hh = Math.floor(h);
    var mm = Math.round((h - hh) * 60);
    return (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm;
}

function parseTimeToHours(value) {
    if (value == null) return null;
    if (typeof value === 'number') {
        if (value > 100000) {
            var d = new Date(value * 1000);
            return d.getHours() + d.getMinutes() / 60;
        }
        return value;
    }
    if (typeof value === 'string') {
        var parts = value.split(':');
        return parseInt(parts[0], 10) + (parseInt(parts[1] || '0', 10) / 60);
    }
    return null;
}

// ─── Slot Cell ────────────────────────────────────────────────
function SlotCell(props) {
    var { hour, resource, booking, statusColors, onClick, onBookingClick, slotStep } = props;

    if (booking) {
        var bgColor = (statusColors && statusColors[booking._status]) || '#7c8db5';
        var entityName = booking.entity_name || '';
        var isStart = booking._isStart;

        return (
            <div
                className={'slot-cell booked' + (isStart ? ' block-start' : ' block-continue')}
                style={{ background: bgColor }}
                onClick={function () { if (onBookingClick) onBookingClick(booking._raw || booking); }}
                title={entityName + (booking.entity_contact_phone ? ' | ' + booking.entity_contact_phone : '') + (booking.code ? ' [' + booking.code + ']' : '')}
            >
                {isStart && (
                    <span className="slot-booking-text">{entityName}</span>
                )}
            </div>
        );
    }

    return (
        <div
            className="slot-cell available"
            onClick={function () {
                if (onClick) onClick({
                    date: resource._selectedDate,
                    startHour: hour,
                    endHour: hour + (slotStep || resource._slotDuration || 1),
                    resourceId: resource.id || resource.code,
                    resourceName: resource.name,
                    resource: resource
                });
            }}
        />
    );
}

// ─── Main SlotGrid ────────────────────────────────────────────
export default function SlotGrid(props) {
    var {
        selectedDate,
        resources,
        bookings,
        startHour,
        endHour,
        slotDuration,
        statusField,
        timeField,
        endTimeField,
        resourceField,
        statusColors,
        onSlotSelect,
        onBookingClick
    } = props;

    var slotStep = (slotDuration || 60) / 60;

    // Generate time slots
    var timeSlots = useMemo(function () {
        var slots = [];
        for (var h = startHour; h < endHour; h += slotStep) {
            slots.push(h);
        }
        return slots;
    }, [startHour, endHour, slotStep]);

    // Map bookings to resource+time
    var bookingMap = useMemo(function () {
        var map = {};
        if (!bookings || !resources) return map;

        for (var i = 0; i < bookings.length; i++) {
            var b = bookings[i];
            // Find which resource this booking belongs to
            var rId = null;
            for (var j = 0; j < resources.length; j++) {
                var r = resources[j];
                var rCode = r.id || r.code;
                // Match via resourceField prop first, then fallback to common fields
                if (resourceField && b[resourceField] === rCode) {
                    rId = rCode;
                    break;
                }
                if (!resourceField && (b.serve_entity_code === rCode || b.serve_place_code === rCode || b.resource_code === rCode)) {
                    rId = rCode;
                    break;
                }
            }
            if (!rId) continue;

            var startH = parseTimeToHours(b[timeField]);
            if (startH == null) continue;

            // Determine duration (default = 1 slot step)
            var duration = slotStep;
            if (endTimeField && b[endTimeField]) {
                var endH = parseTimeToHours(b[endTimeField]);
                if (endH != null && endH > startH) duration = endH - startH;
            } else if (b.duration_minutes) {
                duration = b.duration_minutes / 60;
            } else if (b.end_hour || b.appointment_end_hour) {
                var endH2 = parseTimeToHours(b.end_hour || b.appointment_end_hour);
                if (endH2 != null && endH2 > startH) duration = endH2 - startH;
            }

            var status = statusField ? (b[statusField] || '') : '';

            // Map to slot positions
            for (var s = 0; s < timeSlots.length; s++) {
                var slotH = timeSlots[s];
                if (slotH >= startH && slotH < startH + duration) {
                    var key = rId + '_' + slotH;
                    map[key] = {
                        entity_name: b.entity_name || '',
                        entity_contact_phone: b.entity_contact_phone || '',
                        _status: status,
                        _isStart: Math.abs(slotH - startH) < 0.01,
                        _raw: b,
                        id: b.id,
                        code: b.code
                    };
                }
            }
        }
        return map;
    }, [bookings, resources, timeField, endTimeField, statusField, resourceField, timeSlots, slotStep]);

    // Check if current time is in a slot
    var now = new Date();
    var currentHour = now.getHours() + now.getMinutes() / 60;

    if (!resources || resources.length === 0) {
        return (
            <div className="slot-grid-empty">
                {AntLocalization.translateText({ vi: 'Không có tài nguyên', en: 'No resources', fr: 'Aucune ressource', es: 'Sin recursos', id: 'Tidak ada sumber daya', ja: 'リソースなし', zh: '暂无资源', ko: '리소스 없음', hi: 'कोई संसाधन नहीं', ar: 'لا توجد موارد' })}
            </div>
        );
    }

    return (
        <div className="slot-grid">
            {/* Header row: resource names */}
            <div className="slot-grid-header">
                <div className="slot-time-col header-label">
                    {AntLocalization.translateText({ vi: 'Giờ', en: 'Time', fr: 'Heure', es: 'Hora', id: 'Waktu', ja: '時刻', zh: '时间', ko: '시간', hi: 'समय', ar: 'الوقت' })}
                </div>
                {resources.map(function (r) {
                    var rId = r.id || r.code;
                    return (
                        <div key={rId} className="slot-resource-col">
                            <span className="resource-col-name" title={r.name}>{r.name || r.code}</span>
                        </div>
                    );
                })}
            </div>

            {/* Time rows */}
            <div className="slot-grid-body">
                {timeSlots.map(function (hour) {
                    var isCurrentHour = currentHour >= hour && currentHour < hour + slotStep;
                    var rowClass = 'slot-row' + (isCurrentHour ? ' current-hour' : '');

                    return (
                        <div key={hour} className={rowClass}>
                            <div className="slot-time-col">
                                <span className="slot-time-label">{formatHour(hour)}</span>
                            </div>
                            {resources.map(function (r) {
                                var rId = r.id || r.code;
                                var key = rId + '_' + hour;
                                var booking = bookingMap[key] || null;
                                var enrichedResource = Object.assign({}, r, {
                                    _selectedDate: selectedDate,
                                    _slotDuration: slotStep
                                });

                                return (
                                    <SlotCell
                                        key={key}
                                        hour={hour}
                                        resource={enrichedResource}
                                        booking={booking}
                                        statusColors={statusColors}
                                        slotStep={slotStep}
                                        onClick={onSlotSelect}
                                        onBookingClick={onBookingClick}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
